import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { MatDialog } from '@angular/material/dialog';
import { SwUpdate } from '@angular/service-worker';
import { Subject } from 'rxjs';
import { skip, takeUntil } from 'rxjs/operators';

import version from '../assets/version.json';
import { Chat } from './clinical/inbox-sms/inbox-sms.model';
import { InboxSmsService } from './clinical/inbox-sms/inbox-sms.service';
import { EmailVerificationPromptService } from './services/auth/email-verification-prompt.service';
import { NewAuthService } from './services/auth/new-auth-service.service';
import { LoaderService } from './services/loader.service';
import { SnackService } from './services/snack.service';
import { AreYouSureModalComponent } from './shared/are-you-sure-modal/are-you-sure-modal.component';

export class Group {
  level = 0;
  parent: Group;
  expanded = true;
  totalCounts = 0;
  get visible(): boolean {
    return !this.parent || (this.parent.visible && this.parent.expanded);
  }
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  title = 'welby-web';
  isLoading$ = this.loadingService.isLoadingObservable$;
  private destroy$ = new Subject<void>();

  constructor(
    private db: AngularFirestore,
    private dialog: MatDialog,
    private swUpdate: SwUpdate,
    private authService: NewAuthService,
    private snackService: SnackService,
    private emailVerificationPrompt: EmailVerificationPromptService,
    private loadingService: LoaderService,
    private inboxSMSService: InboxSmsService
  ) {}

  ngOnInit(): void {
    this.authService.needsTakeOverObservable$.pipe(takeUntil(this.destroy$)).subscribe((needsTakeOver) => {
      if (needsTakeOver) {
        this.emailVerificationPrompt.prompt();
      }
    });
  }

  async ngAfterViewInit() {
    // add a 2 second wait
    await new Promise((resolve) => setTimeout(resolve, 2000));
    const webAppVersionSnap = await this.db.collection('_version_').doc('web-app').get().toPromise();
    if (webAppVersionSnap.exists) {
      const major = parseInt(webAppVersionSnap.data()['major'], 10);
      const minor = parseInt(webAppVersionSnap.data()['minor'], 10);
      const patch = parseInt(webAppVersionSnap.data()['patch'], 10);
      if (this.swUpdate.isEnabled) {
        this.swUpdate.available.pipe(takeUntil(this.destroy$)).subscribe(() => {
          const modal = this.dialog.open(AreYouSureModalComponent, {
            data: {
              title: `New version available! ${major}.${minor}.${patch}`,
              body: `There is a new version of the web app available. Please refresh the page. Your current version is : ${version.major}.${version.minor}.${version.patch}`,
              hideCancelBtn: false,
              cancelButton: `I'll refresh later`,
              confirmButton: 'Refresh Now',
            },
          });
          // refresh the page after closed
          modal.afterClosed().subscribe((resp) => {
            if (resp) {
              window.location.reload();
            }
          });
        });
      }
    }
    this.subscribeToNewMessages();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private subscribeToNewMessages(): void {
    if (!this.authService.user?.user_id) {
      return;
    }

    this.inboxSMSService
      .getNewMessage(this.authService.user.user_id)
      .pipe(
        skip(1), // Skip initial emission to only react to new messages
        takeUntil(this.destroy$)
      )
      .subscribe((data: Chat[]) => {
        if (data?.length > 0) {
          const chat = data[0];
          this.snackService.snackBarWithIcon(`New message from ${chat.patientName}`, 'sms', ['info-snackbar'], 5000, 'Dismiss', 'right', 'top');
        }
      });
  }
}
